import React, { useEffect, useState } from "react";
import { Col, Row } from "react-bootstrap";
import { useLoaderData, useLocation, useNavigate } from "react-router-dom";
import BigHeader from "../components/Elements/BigHeader";
import Spinner from "../components/Elements/Spinner";
import SizeSelector from "../components/SizeSelector";
import Descriptor from "../components/UnitListing/Descriptor";
import * as Constants from "../features/generalConstants";
import { getUnitSizes } from "../features/helpers";
import Location from "../features/rapidstor/entities/Location";
import {
  useGetUnitListQuery,
  usePostTrackEventMutation,
} from "../features/rapidstor/rapidstorApiSlice";
import store from "../store";
import { setUnitData } from "../store/bookingSlice";
import classes from "./UnitSelector.module.scss";
import { useCookies } from "react-cookie";
import {
  RapidStorTrackEvent,
  RapidStorTrackEventRequest,
  options,
} from "../services/rapidstor";
import { createId } from "@paralleldrive/cuid2";

function UnitSelector() {
  const location = useLoaderData() as Location;
  const navigate = useNavigate();
  const { data: unitList, isLoading } = useGetUnitListQuery(location.locationCode);
  const { search } = useLocation();
  const parameters = new URLSearchParams(search);
  const requestedSize = parameters.get("size");
  const [cookies] = useCookies(["user"]);
  const [postTrackEvent] = usePostTrackEventMutation();

  function goToBookingForm(descriptor: Descriptor) {
    const descriptors = unitList?.descriptors as Descriptor[];
    const unitSizes = sizes;
    const selectedDescriptor = descriptor;
    const unitType = descriptor.unitTypes[0];
    store.dispatch(
      setUnitData({
        location,
        unitType,
        unitSizes,
        descriptors,
        selectedDescriptor,
        reservation: false,
      })
    );
    navigate(`/${location.slug}/book`);
  }

  const [sizes, setSizes] = useState<(string | number)[]>([]);
  const [descriptors, setDescriptors] = useState<Descriptor[]>([]);
  const [selectedUnitSize, setSelectedUnitSize] = useState(0);

  const selectSuggestedSize = (unitSizes: (string | number)[]) => {
    if (requestedSize && unitSizes.length) {
      // setSelectedUnitSize(closest(sizes, requestedSize))
      const arr: number[] = [];
      for (const s of unitSizes) {
        if (typeof s === "number") {
          arr.push(s);
        }
      }
      const closest = arr.reduce((a, b) => {
        const aDiff = Math.abs(a - parseInt(requestedSize));
        const bDiff = Math.abs(b - parseInt(requestedSize));

        if (aDiff == bDiff) {
          // Choose largest vs smallest (> vs <)
          return a > b ? a : b;
        } else {
          return bDiff < aDiff ? b : a;
        }
      });
      const f = arr.findIndex((e) => e === closest);
      if (f) {
        setSelectedUnitSize(f);
      }
    }
  };

  const setupSlider = () => {
    if (unitList) {
      if (unitList.descriptors && sizes.length === 0) {
        const sortedList: Descriptor[] = [];
        const sizes: (number | string)[] = [];
        unitList.descriptors.map((d, index) => {
          if (sizes.includes(d.area)) {
            return;
          }
          sizes.push(d.area);
          sortedList.push(unitList.descriptors[index]);
        });
        setSizes(getUnitSizes(sortedList));
        setDescriptors(sortedList);
        if (requestedSize && sortedList) {
          selectSuggestedSize(getUnitSizes(sortedList));
        }
      }
    }
  };

  useEffect(() => {
    setupSlider();
  });

  useEffect(() => {
    const rapidStorTrackEventPayload: RapidStorTrackEvent = {
      trackingId: createId(),
      url: window.location.href,
      referrerUrl: document.referrer,
      userAgent: window.navigator.userAgent,
      action: "unit-listing",
      timestamp: new Date().toISOString(),
    };
    const rapidStorTrackEvent: RapidStorTrackEventRequest = {
      locationToken: location.token,
      corpToken: options.corpToken,
      payload: rapidStorTrackEventPayload,
    };
    postTrackEvent(rapidStorTrackEvent);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (cookies) {
      if (cookies.user && cookies.user.locationCode === location.locationCode) {
        navigate(`/${location.slug}/quote`);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      <Row>
        <Col xs={12} sm={12} md={12}>
          <BigHeader>{Constants.UNIT_SELECTOR.HEADER_TEXT}</BigHeader>
          <div className={classes.other_options}>
            <button
              onClick={() => setSelectedUnitSize((prev) => (prev -= 1))}
              disabled={selectedUnitSize === 0}
            >
              {Constants.UNIT_SELECTOR.SMALLER_TEXT}
            </button>
            <button
              onClick={() => setSelectedUnitSize((prev) => (prev += 1))}
              disabled={selectedUnitSize === sizes.length - 1}
            >
              {Constants.UNIT_SELECTOR.BIGGER_TEXT}
            </button>
          </div>

          <SizeSelector
            sizes={sizes}
            selectedUnitSize={selectedUnitSize}
            setSelectedSizeState={setSelectedUnitSize}
          />

          <Row>
            {!isLoading &&
              descriptors.map((unit, index: number) => {
                if (unit.area == sizes[selectedUnitSize]) {
                  return (
                    <div key={unit._id}>
                      <Descriptor
                        onSelectSizeClicked={() => goToBookingForm(descriptors[index])}
                        locationSlug={location.slug}
                        descriptor={descriptors[index]}
                        buttonText={Constants.UNIT_SELECTOR.BUTTON_TEXT}
                        showFacts
                      />
                    </div>
                  );
                }
              })}
            {isLoading && <Spinner />}
          </Row>
        </Col>
      </Row>
    </>
  );
}

export default UnitSelector;
